import React, { useMemo, useState, Fragment, useCallback, useLayoutEffect, } from 'react';
import { format, subDays } from 'date-fns';
import Animated from 'react-native-reanimated';
import { View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { Spacer, AppView, CardView, Indicator, withAnimatedNavigationHeader, } from 'design-system';
import Text from 'design-system/Text';
import SectionHeader from 'design-system/SectionHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import links from 'utils/links';
import useStyles from 'hooks/useStyles';
import { getCostsAndCharges } from 'actions/invest';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectTradingAccount } from 'features/invest/reducers/selectors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { getYearRange } from './utils';
import { isAndroid, isWeb } from '../../../constants';
const AnnualCostsAndChargesScreen = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const [selectedYear, setSelectedYear] = useState(undefined);
    const [requesting, setRequesting] = useState(false);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const investStartedIsoDate = tradingAccount?.account.timestamp;
    const yearRange = useMemo(() => getYearRange(investStartedIsoDate), [investStartedIsoDate]);
    const handleDownload = useCallback((year) => async () => {
        if (requesting) {
            return;
        }
        try {
            setRequesting(true);
            setSelectedYear(year);
            const thisYear = new Date().getUTCFullYear();
            /**
             * toDate should not be present or future
             */
            const toDate = year === thisYear
                ? format(subDays(new Date(), 1), 'yyyy-MM-dd')
                : `${year}-12-31`;
            const response = await dispatch(getCostsAndCharges(`${year}-01-01`, toDate));
            const url = response?.payload?.url;
            if (url) {
                if (isAndroid) {
                    navigation.navigateDeprecated('Pdf', {
                        url,
                        fileName: `Emma_annual_cost_and_Charges_${year}`,
                    });
                }
                else {
                    links.openLink(url);
                }
            }
        }
        catch {
            // Handled by api layer
        }
        finally {
            setRequesting(false);
        }
    }, [requesting]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={paddingHorizontalStyle}>
            <ScreenInfoHeader title="Download the annual costs & charges statement" subTitle="This statement is provided by Emma and documents the costs and charges incurred while using the Emma invest product"/>
            <Spacer.H8 />
            <SectionHeader horizontalMargins>Select a year</SectionHeader>
            <Spacer.H16 />
            {yearRange.map((year) => (<Fragment key={String(year)}>
                <TouchableBounce onPress={handleDownload(year)}>
                  <CardView withPadding noMarginTop noMarginBottom style={[
                styles.card,
                selectedYear === year && styles.selectedCard,
            ]}>
                    <View style={styles.flex}>
                      <Text Numbers-16 Secondary>
                        {year}
                      </Text>
                    </View>
                    {requesting && selectedYear === year ? (<Indicator dark style={styles.indicator}/>) : null}
                  </CardView>
                </TouchableBounce>
                <Spacer.H16 />
              </Fragment>))}
          </Animated.ScrollView>
        </AppView>);
}, ({ colors }) => ({
    hideTitleOnScroll: true,
    title: 'Annual costs & charges',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    card: {
        borderWidth: 1,
        flexDirection: 'row',
        borderColor: colors.cards.onDark,
    },
    selectedCard: {
        borderColor: colors.elements.brand,
    },
    flex: {
        flex: 1,
    },
    indicator: {
        flex: undefined,
    },
}));
export default AnnualCostsAndChargesScreen;
